import React from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaShieldAlt, FaRocket, FaHeart, FaCheckCircle } from 'react-icons/fa';

const About = () => {
  const teamMembers = [
    {
      name: 'Sarah Johnson',
      role: 'CEO & Founder',
      image: '/images/team/sarah.jpg',
      bio: 'Sarah founded Followers Hub with a vision to make social media growth accessible to everyone.'
    },
    {
      name: 'Mike Chen',
      role: 'CTO & Co-Founder',
      image: '/images/team/mike.jpg',
      bio: 'Mike leads our technical team and ensures our platform delivers the best results.'
    },
    {
      name: 'Emma Davis',
      role: 'Head of Customer Success',
      image: '/images/team/emma.jpg',
      bio: 'Emma ensures every customer has the best experience with our services.'
    }
  ];

  const milestones = [
    {
      year: '2020',
      title: 'Company Founded',
      description: 'Followers Hub was founded with a mission to help businesses and individuals grow their social media presence.'
    },
    {
      year: '2021',
      title: '10,000+ Customers',
      description: 'Reached our first major milestone of serving over 10,000 satisfied customers worldwide.'
    },
    {
      year: '2022',
      title: 'Platform Expansion',
      description: 'Expanded our services to include all major social media platforms with innovative growth solutions.'
    },
    {
      year: '2023',
      title: '50,000+ Customers',
      description: 'Celebrated serving over 50,000 customers and delivering millions of followers worldwide.'
    },
    {
      year: '2024',
      title: 'Global Recognition',
      description: 'Recognized as the leading social media growth platform with industry-leading customer satisfaction.'
    }
  ];

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About Followers Hub
            </h1>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              We're on a mission to help businesses and individuals grow their social media presence with real, effective solutions.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              Our Mission
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              To provide authentic, safe, and effective social media growth solutions that help our customers achieve their goals
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: <FaUsers className="text-3xl" />,
                title: 'Real Growth',
                description: 'We deliver real followers and engagement from authentic accounts'
              },
              {
                icon: <FaShieldAlt className="text-3xl" />,
                title: '100% Safe',
                description: 'All our methods are safe and comply with platform guidelines'
              },
              {
                icon: <FaRocket className="text-3xl" />,
                title: 'Fast Results',
                description: 'Quick delivery with guaranteed results within specified timeframes'
              },
              {
                icon: <FaHeart className="text-3xl" />,
                title: 'Customer First',
                description: 'We prioritize customer satisfaction above everything else'
              }
            ].map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{value.title}</h3>
                <p className="text-gray-600">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-6">
                Our Story
              </h2>
              <p className="text-gray-600 text-lg mb-6">
                Followers Hub started as a small idea to help local businesses grow their social media presence. We noticed that many businesses struggled to gain traction online, and we wanted to create a solution that was both effective and accessible.
              </p>
              <p className="text-gray-600 text-lg mb-6">
                Today, we've grown into a global platform serving thousands of customers across multiple social media platforms. Our commitment to quality, safety, and customer satisfaction remains at the core of everything we do.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/packages" className="btn-primary">
                  View Our Services
                </Link>
                <Link to="/contact" className="btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <div className="w-full h-96 bg-gradient-to-br from-purple-200 to-purple-100 rounded-2xl flex items-center justify-center">
                <div className="text-center">
                  <div className="text-6xl font-bold text-purple-800 mb-4">50K+</div>
                  <div className="text-xl text-purple-700">Happy Customers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              Our Journey
            </h2>
            <p className="text-gray-600 text-lg">
              Key milestones in our growth story
            </p>
          </div>

          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-0.5 h-full bg-purple-200"></div>
            
            <div className="space-y-12">
              {milestones.map((milestone, index) => (
                <div key={index} className="relative flex items-center lg:grid lg:grid-cols-2 lg:gap-8">
                  <div className="hidden lg:block"></div>
                  <div className="flex items-center space-x-4 lg:space-x-0">
                    <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                      {milestone.year}
                    </div>
                    <div className="card p-6 flex-1">
                      <h3 className="text-xl font-semibold text-gray-800 mb-2">{milestone.title}</h3>
                      <p className="text-gray-600">{milestone.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold gradient-text mb-4">
              Meet Our Team
            </h2>
            <p className="text-gray-600 text-lg">
              The passionate people behind Followers Hub
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="card p-6 text-center">
                <div className="w-24 h-24 bg-gradient-to-br from-purple-200 to-purple-300 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-3xl font-bold text-purple-800">
                    {member.name.split(' ').map(n => n[0]).join('')}
                  </span>
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">{member.name}</h3>
                <p className="text-purple-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600">{member.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-purple-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Our Impact
            </h2>
            <p className="text-purple-100 text-lg">
              Numbers that speak for themselves
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '50K+', label: 'Happy Customers' },
              { number: '10M+', label: 'Followers Delivered' },
              { number: '99.9%', label: 'Success Rate' },
              { number: '24/7', label: 'Support Available' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.number}</div>
                <div className="text-purple-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 hero-gradient text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Grow Your Social Media?
          </h2>
          <p className="text-xl mb-8 text-purple-100">
            Join thousands of satisfied customers who trust Followers Hub for their social media growth
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/packages" className="btn-primary bg-white text-purple-600 hover:bg-purple-50">
              Get Started Now
            </Link>
            <Link to="/contact" className="btn-secondary border-white text-white hover:bg-white hover:text-purple-600">
              Learn More
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
